<template>
    <div class="tw-h-full tw-flex tw-flex-col tw-space-y-3">
        <!-- Progress Indicator -->
        <div class="tw-flex-shrink-0">
            <div class="tw-flex tw-items-center tw-justify-between tw-text-sm tw-text-gray-600 tw-mb-2">
                <span>{{ $t('onboarding.progress.step_of', { current: 1, total: 4 }) }}</span>
                <span class="tw-hidden sm:tw-block">{{ $t('onboarding.progress.language_selection') }}</span>
                <span class="tw-block sm:tw-hidden">{{ $t('onboarding.progress.language') }}</span>
            </div>
            <div class="tw-w-full tw-bg-gray-200 tw-rounded-full tw-h-2">
                <div class="tw-bg-primary-600 tw-h-2 tw-rounded-full tw-transition-all tw-duration-300" style="width: 25%"></div>
            </div>
        </div>

        <!-- Scrollable Content -->
        <div class="tw-flex-1 tw-overflow-y-auto tw-space-y-4">
            <!-- Welcome Message -->
            <div class="tw-bg-primary-50 tw-border tw-border-primary-200 tw-rounded-lg tw-p-3">
                <div class="tw-flex tw-items-start tw-gap-2.5">
                    <icon icon="solar:global-bold-duotone" class="tw-text-primary-600 tw-text-base tw-mt-0.5 tw-flex-shrink-0" />
                    <div>
                        <h3 class="tw-text-sm tw-font-medium tw-text-primary-800 tw-mb-0.5">
                            {{ $t('onboarding.language.title') }}
                        </h3>
                        <p class="tw-text-xs tw-text-primary-700 tw-leading-tight">
                            {{ $t('onboarding.language.description') }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Language Options -->
            <div class="tw-space-y-2">
                <!-- English -->
                <button
                    @click="selectLanguage('en')"
                    :class="[
                        'tw-w-full tw-p-4 tw-rounded-lg tw-border tw-border-solid tw-transition-all tw-duration-200 tw-flex tw-items-center tw-gap-4 tw-text-left',
                        props.formData.language === 'en' 
                            ? 'tw-border-primary-400 tw-bg-gradient-to-r tw-from-primary-50 tw-to-primary-100' 
                            : 'tw-border-gray-150 tw-bg-white hover:tw-border-gray-250 hover:tw-bg-gray-25'
                    ]"
                >
                    <div class="tw-w-10 tw-h-7 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                        <icon icon="emojione-v1:flag-for-united-states" class="tw-text-2xl" />
                    </div>
                    <div class="tw-flex-1">
                        <div :class="[
                            'tw-font-medium tw-text-base',
                            props.formData.language === 'en' ? 'tw-text-primary-800' : 'tw-text-gray-800'
                        ]">{{ $t('onboarding.language.english') }}</div>
                        <div :class="[
                            'tw-text-sm tw-mt-0.5',
                            props.formData.language === 'en' ? 'tw-text-primary-600' : 'tw-text-gray-500'
                        ]">{{ $t('onboarding.language.english_continue') }}</div>
                    </div>
                    <div class="tw-flex-shrink-0">
                        <div :class="[
                            'tw-w-5 tw-h-5 tw-rounded-full tw-border-2 tw-transition-all tw-duration-200 tw-flex tw-items-center tw-justify-center',
                            props.formData.language === 'en' 
                                ? 'tw-border-primary-500 tw-bg-primary-500' 
                                : 'tw-border-gray-300'
                        ]">
                            <icon v-if="props.formData.language === 'en'" icon="heroicons-solid:check" class="tw-text-white tw-text-xs" />
                        </div>
                    </div>
                </button>

                <!-- French -->
                <button
                    @click="selectLanguage('fr')"
                    :class="[
                        'tw-w-full tw-p-4 tw-rounded-lg tw-border tw-border-solid tw-transition-all tw-duration-200 tw-flex tw-items-center tw-gap-4 tw-text-left',
                        props.formData.language === 'fr' 
                            ? 'tw-border-primary-400 tw-bg-gradient-to-r tw-from-primary-50 tw-to-primary-100' 
                            : 'tw-border-gray-150 tw-bg-white hover:tw-border-gray-250 hover:tw-bg-gray-25'
                    ]"
                >
                    <div class="tw-w-10 tw-h-7 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                        <icon icon="emojione-v1:flag-for-france" class="tw-text-2xl" />
                    </div>
                    <div class="tw-flex-1">
                        <div :class="[
                            'tw-font-medium tw-text-base',
                            props.formData.language === 'fr' ? 'tw-text-primary-800' : 'tw-text-gray-800'
                        ]">{{ $t('onboarding.language.french') }}</div>
                        <div :class="[
                            'tw-text-sm tw-mt-0.5',
                            props.formData.language === 'fr' ? 'tw-text-primary-600' : 'tw-text-gray-500'
                        ]">{{ $t('onboarding.language.french_continue') }}</div>
                    </div>
                    <div class="tw-flex-shrink-0">
                        <div :class="[
                            'tw-w-5 tw-h-5 tw-rounded-full tw-border-2 tw-transition-all tw-duration-200 tw-flex tw-items-center tw-justify-center',
                            props.formData.language === 'fr' 
                                ? 'tw-border-primary-500 tw-bg-primary-500' 
                                : 'tw-border-gray-300'
                        ]">
                            <icon v-if="props.formData.language === 'fr'" icon="heroicons-solid:check" class="tw-text-white tw-text-xs" />
                        </div>
                    </div>
                </button>

                <!-- Spanish -->
                <button
                    @click="selectLanguage('es')"
                    :class="[
                        'tw-w-full tw-p-4 tw-rounded-lg tw-border tw-border-solid tw-transition-all tw-duration-200 tw-flex tw-items-center tw-gap-4 tw-text-left',
                        props.formData.language === 'es' 
                            ? 'tw-border-primary-400 tw-bg-gradient-to-r tw-from-primary-50 tw-to-primary-100' 
                            : 'tw-border-gray-150 tw-bg-white hover:tw-border-gray-250 hover:tw-bg-gray-25'
                    ]"
                >
                    <div class="tw-w-10 tw-h-7 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                        <icon icon="emojione-v1:flag-for-spain" class="tw-text-2xl" />
                    </div>
                    <div class="tw-flex-1">
                        <div :class="[
                            'tw-font-medium tw-text-base',
                            props.formData.language === 'es' ? 'tw-text-primary-800' : 'tw-text-gray-800'
                        ]">{{ $t('onboarding.language.spanish') }}</div>
                        <div :class="[
                            'tw-text-sm tw-mt-0.5',
                            props.formData.language === 'es' ? 'tw-text-primary-600' : 'tw-text-gray-500'
                        ]">{{ $t('onboarding.language.spanish_continue') }}</div>
                    </div>
                    <div class="tw-flex-shrink-0">
                        <div :class="[
                            'tw-w-5 tw-h-5 tw-rounded-full tw-border-2 tw-transition-all tw-duration-200 tw-flex tw-items-center tw-justify-center',
                            props.formData.language === 'es' 
                                ? 'tw-border-primary-500 tw-bg-primary-500' 
                                : 'tw-border-gray-300'
                        ]">
                            <icon v-if="props.formData.language === 'es'" icon="heroicons-solid:check" class="tw-text-white tw-text-xs" />
                        </div>
                    </div>
                </button>

                <!-- Portuguese -->
                <button
                    @click="selectLanguage('pt')"
                    :class="[
                        'tw-w-full tw-p-4 tw-rounded-lg tw-border tw-border-solid tw-transition-all tw-duration-200 tw-flex tw-items-center tw-gap-4 tw-text-left',
                        props.formData.language === 'pt' 
                            ? 'tw-border-primary-400 tw-bg-gradient-to-r tw-from-primary-50 tw-to-primary-100' 
                            : 'tw-border-gray-150 tw-bg-white hover:tw-border-gray-250 hover:tw-bg-gray-25'
                    ]"
                >
                    <div class="tw-w-10 tw-h-7 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                        <icon icon="emojione-v1:flag-for-portugal" class="tw-text-2xl" />
                    </div>
                    <div class="tw-flex-1">
                        <div :class="[
                            'tw-font-medium tw-text-base',
                            props.formData.language === 'pt' ? 'tw-text-primary-800' : 'tw-text-gray-800'
                        ]">{{ $t('onboarding.language.portuguese') }}</div>
                        <div :class="[
                            'tw-text-sm tw-mt-0.5',
                            props.formData.language === 'pt' ? 'tw-text-primary-600' : 'tw-text-gray-500'
                        ]">{{ $t('onboarding.language.portuguese_continue') }}</div>
                    </div>
                    <div class="tw-flex-shrink-0">
                        <div :class="[
                            'tw-w-5 tw-h-5 tw-rounded-full tw-border-2 tw-transition-all tw-duration-200 tw-flex tw-items-center tw-justify-center',
                            props.formData.language === 'pt' 
                                ? 'tw-border-primary-500 tw-bg-primary-500' 
                                : 'tw-border-gray-300'
                        ]">
                            <icon v-if="props.formData.language === 'pt'" icon="heroicons-solid:check" class="tw-text-white tw-text-xs" />
                        </div>
                    </div>
                </button>
            </div>

            <!-- Info Notice -->
            <div class="tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-p-2">
                <div class="tw-flex tw-items-start tw-gap-2">
                    <icon icon="solar:info-circle-bold-duotone" class="tw-text-gray-500 tw-text-xs tw-mt-0.5 tw-flex-shrink-0" />
                    <p class="tw-text-xs tw-text-gray-600 tw-leading-tight">
                        {{ $t('onboarding.language.note') }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
    formData: {
        type: Object,
        required: true
    },
    errors: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['update:formData']);
const i18n = useI18n();

const selectLanguage = (language) => {
    // Update the form data
    const newFormData = { ...props.formData, language };
    emit('update:formData', newFormData);
    
    // Change the interface language immediately
    i18n.locale.value = language;
};
</script>

<style scoped>
/* Add any specific styles if needed */
</style> 